import { NoteRecord, OfflineRecord, SyncMeta, UserDictionaryEntry, DictionaryMapping, OfflineTrainingLog } from "./types";

const DB_NAME = "blackapp_offline";
const DB_VERSION = 2;

const STORES = {
  rawAudio: "raw_audio",
  offlineNotes: "offline_notes",
  syncQueue: "sync_queue",
  offlineIndex: "offline_search_index",
  syncMeta: "offline_sync_meta",
  userDictionaries: "user_dictionaries",
  dictionaryMappings: "dictionary_mappings",
  offlineTrainingLog: "offline_training_log"
} as const;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (typeof window === "undefined" || !("indexedDB" in window)) {
    return Promise.resolve(null);
  }

  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORES.rawAudio)) {
        const s = db.createObjectStore(STORES.rawAudio, { keyPath: "hash" });
        s.createIndex("recorded_at", "recordedAt");
      }

      if (!db.objectStoreNames.contains(STORES.offlineNotes)) {
        const s = db.createObjectStore(STORES.offlineNotes, { keyPath: "id" });
        s.createIndex("employee_name", "entities.employee");
        s.createIndex("created_at", "createdAt");
      }

      if (!db.objectStoreNames.contains(STORES.syncQueue)) {
        const s = db.createObjectStore(STORES.syncQueue, { keyPath: "noteId" });
        s.createIndex("added_at", "addedAt");
      }

      if (!db.objectStoreNames.contains(STORES.offlineIndex)) {
        const s = db.createObjectStore(STORES.offlineIndex, { keyPath: "id" });
        s.createIndex("record_type", "recordType");
        s.createIndex("record_date", "recordDate");
        s.createIndex("employee_name", "employeeName");
      }

      if (!db.objectStoreNames.contains(STORES.syncMeta)) {
        db.createObjectStore(STORES.syncMeta, { keyPath: "key" });
      }

      if (!db.objectStoreNames.contains(STORES.userDictionaries)) {
        const s = db.createObjectStore(STORES.userDictionaries, { keyPath: "id" });
        s.createIndex("industry_category", ["industry", "category"]);
        s.createIndex("term", "term");
        s.createIndex("managerId", "managerId");
      }

      if (!db.objectStoreNames.contains(STORES.dictionaryMappings)) {
        const s = db.createObjectStore(STORES.dictionaryMappings, { keyPath: "id" });
        s.createIndex("rawToken", "rawToken");
        s.createIndex("resolvedCanonical", "resolvedCanonical");
      }

      if (!db.objectStoreNames.contains(STORES.offlineTrainingLog)) {
        const s = db.createObjectStore(STORES.offlineTrainingLog, { keyPath: "id" });
        s.createIndex("synced", "synced");
        s.createIndex("created_at", "createdAt");
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return dbPromise;
}

const memoryStore = {
  notes: new Map<string, NoteRecord>(),
  queue: new Map<string, { noteId: string; addedAt: number }>(),
  rawAudio: new Map<string, unknown>(),
  searchIndex: new Map<string, OfflineRecord>(),
  meta: new Map<string, SyncMeta>(),
  userDictionaries: new Map<string, UserDictionaryEntry>(),
  dictionaryMappings: new Map<string, DictionaryMapping>(),
  trainingLog: new Map<string, OfflineTrainingLog>()
};

async function withStore<T>(
  storeName: string,
  mode: "readonly" | "readwrite",
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDB();
  if (!db) throw new Error("IndexedDB not available");
  const tx = db.transaction(storeName, mode);
  const store = tx.objectStore(storeName);
  return new Promise((resolve, reject) => {
    const req = fn(store);
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  });
}

export async function saveNote(note: NoteRecord): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.notes.set(note.id, note);
    return;
  }
  await withStore(STORES.offlineNotes, "readwrite", s => s.put(note));
}

export async function getNote(id: string): Promise<NoteRecord | undefined> {
  const db = await openDB();
  if (!db) return memoryStore.notes.get(id);
  const res = await withStore<NoteRecord | undefined>(STORES.offlineNotes, "readonly", s => s.get(id));
  return res ?? undefined;
}

export async function getNotes(): Promise<NoteRecord[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.notes.values()];
  const res = await withStore<NoteRecord[]>(STORES.offlineNotes, "readonly", s => s.getAll());
  return res ?? [];
}

export async function updateNote(note: NoteRecord): Promise<void> {
  await saveNote(note);
}

export async function deleteNote(id: string): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.notes.delete(id);
    return;
  }
  await withStore(STORES.offlineNotes, "readwrite", s => s.delete(id));
}

export async function enqueue(noteId: string): Promise<void> {
  const item = { noteId, addedAt: Date.now() };
  const db = await openDB();
  if (!db) {
    memoryStore.queue.set(noteId, item);
    return;
  }
  await withStore(STORES.syncQueue, "readwrite", s => s.put(item));
}

export async function dequeue(noteId: string): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.queue.delete(noteId);
    return;
  }
  await withStore(STORES.syncQueue, "readwrite", s => s.delete(noteId));
}

export async function getQueue(): Promise<string[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.queue.keys()];
  const res = await withStore<{ noteId: string }[]>(STORES.syncQueue, "readonly", s => s.getAll());
  return (res ?? []).map(x => x.noteId);
}

export async function saveRawAudio(hash: string, blob: Blob, recordedAt: number, noteId?: string): Promise<void> {
  const db = await openDB();
  const rec = { hash, blob, recordedAt, noteId, synced: false };
  if (!db) {
    memoryStore.rawAudio.set(hash, rec);
    return;
  }
  await withStore(STORES.rawAudio, "readwrite", s => s.put(rec));
}

export async function getRawAudio(hash: string): Promise<Blob | undefined> {
  const db = await openDB();
  if (!db) {
    const rec = memoryStore.rawAudio.get(hash) as { blob: Blob } | undefined;
    return rec?.blob;
  }
  const res = await withStore<{ blob: Blob } | undefined>(STORES.rawAudio, "readonly", s => s.get(hash));
  return res?.blob;
}

// Usuwa lokalną kopię nagrania po udanym wysłaniu do backendu — telefon nie
// powinien w nieskończoność gromadzić głosówek offline.
export async function deleteRawAudio(hash: string): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.rawAudio.delete(hash);
    return;
  }
  await withStore(STORES.rawAudio, "readwrite", s => s.delete(hash));
}

// Wszystkie nagrania (online + offline) jeszcze niewysłane na serwer — niezależnie
// od tego, czy są powiązane z konkretną notatką (ścieżka A online dziś nie tworzy
// lokalnego NoteRecord, więc audio trzeba synchronizować osobno od notatek).
export async function getUnsyncedRawAudio(): Promise<{ hash: string; blob: Blob; noteId?: string }[]> {
  const db = await openDB();
  if (!db) {
    return [...memoryStore.rawAudio.values()]
      .filter((r: any) => !r.synced)
      .map((r: any) => ({ hash: r.hash, blob: r.blob, noteId: r.noteId }));
  }
  const all = await withStore<{ hash: string; blob: Blob; noteId?: string; synced?: boolean }[]>(
    STORES.rawAudio, "readonly", s => s.getAll()
  );
  return (all ?? []).filter(r => !r.synced);
}

export async function markRawAudioSynced(hash: string): Promise<void> {
  const db = await openDB();
  if (!db) {
    const rec = memoryStore.rawAudio.get(hash) as any;
    if (rec) rec.synced = true;
    return;
  }
  const rec = await withStore<any>(STORES.rawAudio, "readonly", s => s.get(hash));
  if (!rec) return;
  rec.synced = true;
  await withStore(STORES.rawAudio, "readwrite", s => s.put(rec));
}

export async function saveSearchIndex(rec: OfflineRecord): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.searchIndex.set(rec.id, rec);
    return;
  }
  await withStore(STORES.offlineIndex, "readwrite", s => s.put(rec));
}

export async function getSearchIndexes(): Promise<OfflineRecord[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.searchIndex.values()];
  const res = await withStore<OfflineRecord[]>(STORES.offlineIndex, "readonly", s => s.getAll());
  return res ?? [];
}

export async function clearSearchIndex(): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.searchIndex.clear();
    return;
  }
  await withStore(STORES.offlineIndex, "readwrite", s => s.clear());
}

export async function setSyncMeta(meta: SyncMeta): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.meta.set("default", meta);
    return;
  }
  await withStore(STORES.syncMeta, "readwrite", s => s.put({ key: "default", ...meta }));
}

export async function getSyncMeta(): Promise<SyncMeta | undefined> {
  const db = await openDB();
  if (!db) return memoryStore.meta.get("default");
  const res = await withStore<SyncMeta | undefined>(STORES.syncMeta, "readonly", s => s.get("default"));
  return res ?? undefined;
}

export async function saveUserDictionary(entry: UserDictionaryEntry): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.userDictionaries.set(entry.id, entry);
    return;
  }
  await withStore(STORES.userDictionaries, "readwrite", s => s.put(entry));
}

export async function getUserDictionaries(): Promise<UserDictionaryEntry[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.userDictionaries.values()];
  const res = await withStore<UserDictionaryEntry[]>(STORES.userDictionaries, "readonly", s => s.getAll());
  return res ?? [];
}

export async function getUserDictionariesByIndustry(
  industry: string,
  plantId?: string,
  managerId?: number
): Promise<UserDictionaryEntry[]> {
  const all = await getUserDictionaries();
  return all.filter(e =>
    e.industry === industry &&
    (!plantId || e.plantId === plantId) &&
    (e.scope === "plant" || (e.scope === "user" && (!managerId || e.managerId === managerId)))
  );
}

export async function clearUserDictionaries(): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.userDictionaries.clear();
    return;
  }
  await withStore(STORES.userDictionaries, "readwrite", s => s.clear());
}

export async function saveDictionaryMapping(mapping: DictionaryMapping): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.dictionaryMappings.set(mapping.id, mapping);
    return;
  }
  await withStore(STORES.dictionaryMappings, "readwrite", s => s.put(mapping));
}

export async function getDictionaryMappings(): Promise<DictionaryMapping[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.dictionaryMappings.values()];
  const res = await withStore<DictionaryMapping[]>(STORES.dictionaryMappings, "readonly", s => s.getAll());
  return res ?? [];
}

export async function findDictionaryMapping(rawToken: string): Promise<DictionaryMapping | undefined> {
  const db = await openDB();
  if (!db) return [...memoryStore.dictionaryMappings.values()].find(m => m.rawToken === rawToken);
  const res = await withStore<DictionaryMapping | undefined>(STORES.dictionaryMappings, "readonly", s => s.index("rawToken").get(rawToken));
  return res ?? undefined;
}

export async function saveTrainingLog(entry: OfflineTrainingLog): Promise<void> {
  const db = await openDB();
  if (!db) {
    memoryStore.trainingLog.set(entry.id, entry);
    return;
  }
  await withStore(STORES.offlineTrainingLog, "readwrite", s => s.put(entry));
}

export async function getTrainingLog(): Promise<OfflineTrainingLog[]> {
  const db = await openDB();
  if (!db) return [...memoryStore.trainingLog.values()];
  const res = await withStore<OfflineTrainingLog[]>(STORES.offlineTrainingLog, "readonly", s => s.getAll());
  return res ?? [];
}

export async function getUnsyncedTrainingLog(): Promise<OfflineTrainingLog[]> {
  const all = await getTrainingLog();
  return all.filter(e => !e.synced);
}
